import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, ExternalLink, ListMusic } from "lucide-react";
import { api } from "../lib/api";
import { useMediaPlayer, classifyMedia } from "../lib/player";

/**
 * Homepage "Press Play" rail. Pulls a few playable items (playlists + podcasts)
 * and lets visitors play them inline via the global MiniPlayer without leaving
 * the homepage. Uses the existing /api/media endpoint.
 */
export default function FeaturedMedia() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const { playItem, current, playing, toggle } = useMediaPlayer();

    useEffect(() => {
        (async () => {
            try {
                // Pull both playlists and podcasts; filter to truly playable items.
                const [pl, pod] = await Promise.all([
                    api.get("/media", { params: { type: "playlist", limit: 12 } }),
                    api.get("/media", { params: { type: "podcast", limit: 6 } }),
                ]);
                const combined = [...pl.data, ...pod.data]
                    .filter((m) => ["spotify", "audio"].includes(classifyMedia(m.media_url)))
                    .slice(0, 6);
                setItems(combined);
            } catch {
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (!loading && items.length === 0) return null;

    const handlePlay = (m) => {
        const queueable = items.map((x) => ({
            id: x.id,
            title: x.title,
            host: x.host,
            image: x.image_url,
            url: x.media_url,
        }));
        const me = queueable.find((x) => x.id === m.id);
        // Toggle pause/play if this card is already the active track
        if (current?.id === m.id) {
            toggle();
            return;
        }
        playItem(me, queueable);
    };

    return (
        <section data-testid="featured-media-section" className="mt-12 border-t border-streetz-line pt-10">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="overline text-streetz-yellow">PRESS PLAY</div>
                    <h2 className="font-display uppercase text-3xl md:text-4xl mt-1">Featured on MyStreetz Radio</h2>
                    <p className="text-streetz-muted text-sm mt-1">
                        Tap to play right here — the mini-player slides up from the bottom.
                    </p>
                </div>
                <Link
                    to="/m/playlists"
                    data-testid="featured-media-see-all"
                    className="overline text-streetz-muted hover:text-streetz-yellow hidden md:inline-flex items-center gap-1"
                >
                    SEE ALL <ExternalLink className="w-3 h-3" />
                </Link>
            </div>

            {loading ? (
                <p className="text-streetz-muted">Loading…</p>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-media-grid">
                    {items.map((m) => {
                        const isActive = current?.id === m.id;
                        const isPlaying = isActive && playing;
                        return (
                            <button
                                key={m.id}
                                type="button"
                                onClick={() => handlePlay(m)}
                                data-testid={`featured-media-${m.slug}`}
                                className={`group text-left bg-streetz-paper border transition-colors flex flex-col ${
                                    isActive ? "border-streetz-yellow" : "border-streetz-line hover:border-streetz-yellow"
                                }`}
                            >
                                <div className="relative">
                                    {m.image_url ? (
                                        <img src={m.image_url} alt={m.title} className="aspect-video object-cover w-full" />
                                    ) : (
                                        <div className="aspect-video bg-streetz-line grid place-items-center">
                                            <ListMusic className="w-8 h-8 text-streetz-yellow" />
                                        </div>
                                    )}
                                    <div
                                        className={`absolute bottom-3 right-3 h-12 w-12 grid place-items-center rounded-full shadow-lg transition-colors ${
                                            isPlaying
                                                ? "bg-streetz-yellow text-black"
                                                : "bg-streetz-red text-white group-hover:bg-streetz-yellow group-hover:text-black"
                                        }`}
                                        aria-label={isPlaying ? "Pause" : "Play"}
                                    >
                                        {isPlaying ? (
                                            <Pause className="w-5 h-5" />
                                        ) : (
                                            <Play className="w-5 h-5 ml-[2px]" />
                                        )}
                                    </div>
                                </div>
                                <div className="p-4 flex-1 flex flex-col">
                                    <span className="overline text-streetz-red text-[10px]">{m.host || m.type}</span>
                                    <h3 className="font-display uppercase text-lg leading-tight mt-1 group-hover:text-streetz-yellow">
                                        {m.title}
                                    </h3>
                                    <p className="text-xs text-streetz-muted mt-1 line-clamp-2 flex-1">{m.description}</p>
                                    <div className="overline text-[10px] text-streetz-muted mt-3">
                                        {isPlaying ? "NOW PLAYING" : isActive ? "PAUSED" : "TAP TO PLAY"}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}
        </section>
    );
}
